import { useLayoutEffect, useRef, useState } from "react";

const TOLERANCE = 1;

export type ScrollEdges = { isScrollAtTop: boolean; isScrollAtBottom: boolean };

export const AT_BOTH_EDGES: ScrollEdges = { isScrollAtTop: true, isScrollAtBottom: true };

const readScrollEdges = (scroller: HTMLElement | null): ScrollEdges =>
  scroller
    ? {
        isScrollAtTop: scroller.scrollTop <= TOLERANCE,
        isScrollAtBottom:
          scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - TOLERANCE,
      }
    : AT_BOTH_EDGES;

const keepIfUnchanged = (next: ScrollEdges) => (previous: ScrollEdges) =>
  previous.isScrollAtTop === next.isScrollAtTop &&
  previous.isScrollAtBottom === next.isScrollAtBottom
    ? previous
    : next;

/** Suit les bords de défilement d'un élément : `scrollerRef` à lui donner, `onScroll` à brancher dessus. */
export const useScrollEdges = <T extends HTMLElement>() => {
  const scrollerRef = useRef<T>(null);
  const [edges, setEdges] = useState(AT_BOTH_EDGES);

  const onScroll = () => setEdges(keepIfUnchanged(readScrollEdges(scrollerRef.current)));

  // Le contenu a pu changer de hauteur à chaque rendu.
  useLayoutEffect(onScroll);

  // Le scroller change de hauteur avec la fenêtre.
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }
    const observer = new ResizeObserver(() => setEdges(keepIfUnchanged(readScrollEdges(scroller))));
    observer.observe(scroller);
    return () => observer.disconnect();
  }, []);

  return { scrollerRef, ...edges, onScroll };
};
